import { useEffect, useId, useRef, useState } from "react";

import {
  forgetFormState,
  hasUnsavedInput,
  rememberFormState,
} from "../utils/unsavedInput";

// Everything that can hold focus inside the dialog. `tabindex="-1"` is excluded on purpose: it is
// what the overlay itself carries, so it takes programmatic focus without becoming a tab stop.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

// The question every dismissal this component owns has to ask first, so that no route out of the
// dialog can discard what another route would have asked about. The close button is deliberately
// not on it: it is an explicit, labelled "close this", and the X in the jQuery modals dismisses
// outright too.
const mayDiscard = (dialog) =>
  !hasUnsavedInput(dialog) ||
  window.confirm(
    window.I18n?.t?.("shared.discard_unsaved_changes") ||
      "Discard your unsaved changes?",
  );

const Modal = ({ show, title, children, onHide }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevShowRef = useRef(show);
  const modalRef = useRef(null);
  const mouseDownTargetRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const revealFrameRef = useRef(null);
  const titleId = useId();

  const cancelReveal = () => {
    if (revealFrameRef.current === null) return;

    cancelAnimationFrame(revealFrameRef.current);
    revealFrameRef.current = null;
  };

  useEffect(() => cancelReveal, []);

  useEffect(() => {
    const prevShow = prevShowRef.current;

    // Show modal if transitioning from hidden to shown
    if (!prevShow && show) {
      setIsVisible(true);

      // Start animation after render
      revealFrameRef.current = requestAnimationFrame(() => {
        revealFrameRef.current = null;
        setIsAnimating(true);
      });
    }

    // Hide modal if transitioning from shown to hidden
    if (prevShow && !show) {
      if (onHide) onHide();
      // A close that lands before the reveal frame runs would otherwise flip isAnimating back on
      // a frame later, handing a dialog that is already going away to the focus effects.
      cancelReveal();
      setIsAnimating(false);

      // Wait for animation to complete before hiding
      setTimeout(() => {
        setIsVisible(false);
      }, 150);
    }

    prevShowRef.current = show;
  }, [show, onHide]);

  // Move focus into the dialog while it is open, and hand it back afterwards. Without this the
  // element that opened the modal keeps focus behind the overlay -- or, if opening the modal
  // unmounted it, focus falls to the document body.
  //
  // Keyed on isAnimating rather than isVisible: the dialog is aria-hidden for the frame between
  // mounting and the reveal, and focus must not land inside a subtree hidden from assistive
  // technology. On close this also hands focus back as the dialog starts fading, not after.
  useEffect(() => {
    if (!isAnimating) return undefined;

    const dialog = modalRef.current;
    const opener = document.activeElement;
    // The body is not a place to send anyone: it means whatever opened the dialog is already
    // gone, and focusing it would only strip focus from wherever it has since landed.
    lastFocusedRef.current = opener === document.body ? null : opener;
    if (dialog) dialog.focus();
    // What Escape compares against. Taken here rather than on mount, because this is the point at
    // which the dialog is actually on screen and its children have rendered -- and children whose
    // fields arrive later still, such as ShiftForm filling every control from its `shift` prop in
    // an effect of its own, are picked up by the watcher this arms.
    rememberFormState(dialog);

    return () => {
      // Held rather than read back off the ref: by the time this runs the ref may already point
      // somewhere else, and this is the dialog whose watcher is being let go.
      forgetFormState(dialog);
      const previous = lastFocusedRef.current;
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [isAnimating]);

  // Keep Tab inside the dialog while it is open. Without this, tabbing past the last control walks
  // into the page behind the overlay -- content the user cannot see and should not be operating.
  useEffect(() => {
    if (!isAnimating) return undefined;

    const handleTab = (event) => {
      if (event.key !== "Tab" || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      const active = document.activeElement;

      if (focusable.length === 0) {
        // Nothing to land on -- park focus on the dialog itself rather than letting it escape.
        event.preventDefault();
        modalRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // The overlay holds focus itself right after opening. contains() counts a node as its own
      // descendant, so it has to be excluded here or Shift+Tab from that starting point matches
      // no branch at all and walks off into the page behind.
      const inside =
        active !== modalRef.current && modalRef.current.contains(active);

      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [isAnimating]);

  // Handle ESC key. It closes an untouched dialog outright and asks first when there is something
  // to lose -- the same rule Widgets.Modal applies, so that the key means one thing in this app
  // rather than depending on which stack drew the dialog.
  //
  // Keyed on isAnimating, like the focus and Tab effects, rather than on `show`: what Escape
  // compares against is taken in the effect above, which does not run until the reveal frame. Bound
  // any earlier, Escape pressed in that first frame would find a form with no snapshot, read it as
  // unsaved, and ask about a dialog that had only just appeared.
  useEffect(() => {
    if (!isAnimating) return undefined;

    const handleEscape = (e) => {
      if (e.key !== "Escape" || !onHide) return;
      if (!mayDiscard(modalRef.current)) return;

      onHide();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isAnimating, onHide]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const handleBackdropMouseDown = (e) => {
    // Track where the mouse was pressed down
    mouseDownTargetRef.current = e.target;
  };

  const handleBackdropClick = (e) => {
    // Only close if both mousedown and mouseup happened on the backdrop itself
    const onBackdrop =
      e.target === modalRef.current &&
      mouseDownTargetRef.current === modalRef.current;
    // Reset the ref
    mouseDownTargetRef.current = null;

    if (!onBackdrop || !onHide) return;
    // A click outside the dialog throws away what is in it as surely as Escape does, so it asks
    // the same question. The jQuery modals keep the same promise a blunter way, with
    // `backdrop: 'static'`, where the click does nothing at all.
    if (!mayDiscard(modalRef.current)) return;

    onHide();
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2002,
    overflow: "auto",
    backgroundColor: isAnimating ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)",
    transition: "background-color 0.15s ease-in-out",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "60px",
    paddingBottom: "60px",
  };

  const dialogStyle = {
    position: "relative",
    width: "600px",
    maxWidth: "90%",
    margin: "0 auto",
    opacity: isAnimating ? 1 : 0,
    transform: isAnimating ? "translateY(0)" : "translateY(-50px)",
    transition: "opacity 0.15s ease-in-out, transform 0.15s ease-in-out",
  };

  const contentStyle = {
    position: "relative",
    backgroundColor: "#fff",
    border: "1px solid rgba(0, 0, 0, 0.2)",
    borderRadius: "6px",
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.5)",
    outline: 0,
  };

  const headerStyle = {
    padding: "15px",
    borderBottom: "1px solid #e5e5e5",
    minHeight: "16.43px",
  };

  const closeButtonStyle = {
    float: "right",
    fontSize: "21px",
    fontWeight: "bold",
    lineHeight: "1",
    color: "#000",
    textShadow: "0 1px 0 #fff",
    opacity: 0.2,
    background: "transparent",
    border: 0,
    cursor: "pointer",
    padding: 0,
    appearance: "none",
  };

  return (
    <div
      ref={modalRef}
      style={overlayStyle}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-hidden={!isAnimating}
    >
      <div style={dialogStyle}>
        <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <button
              type="button"
              style={closeButtonStyle}
              onClick={onHide}
              aria-label="Close"
              onMouseEnter={(e) => (e.target.style.opacity = 0.5)}
              onMouseLeave={(e) => (e.target.style.opacity = 0.2)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 id={titleId} style={{ margin: 0, lineHeight: "1.42857143" }}>
              {title}
            </h4>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
